import Layout from '../components/layout/Layout';
import SectionLabel from '../components/shared/SectionLabel';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import SEO from '../components/shared/SEO';
import Breadcrumb from '../components/shared/Breadcrumb';
import { useSubsidiaries } from '../hooks/useApi';
import { getSubsidiaryUrl } from '../constants/subsidiaryLinks';
import '../styles/subsidiariespage.css';

export default function SubsidiariesPage() {
  const { data: subsidiaries, isLoading } = useSubsidiaries();

  return (
    <Layout>
      <SEO
        title="Subsidiaries - Apex DeFi Labs Inc."
        description="Explore the subsidiary companies of Apex DeFi Labs Inc. spanning decentralized finance, artificial intelligence, analytics, and technology ventures."
        url="https://apexdefilabs.com/subsidiaries"
        type="website"
        keywords="Apex DeFi Labs Inc. subsidiaries, DeFi Assets LLC, AI Adya, Vivasvat Revolutions"
      />

      <div className="subs-page">
        {/* Hero */}
        <section className="subs-hero">
          <div className="subs-hero-overlay" />
          <div className="subs-shell">
            <Breadcrumb items={[
              { label: 'Home', href: '/' },
              { label: 'Subsidiaries' },
            ]} />
            <div style={{ marginBottom: '1.2rem', marginTop: '1.5rem' }}>
              <SectionLabel>Corporate Structure</SectionLabel>
            </div>
            <h1 className="subs-hero-title">
              Our <em>Subsidiaries</em>
            </h1>
            <p className="subs-hero-sub">
              A network of specialized entities operating across decentralized finance,
              artificial intelligence, analytics, and global technology ventures.
            </p>
          </div>
        </section>

        {/* Subsidiaries Grid */}
        <section className="subs-section">
          <div className="subs-shell">
            {isLoading ? (
              <div className="subs-grid">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="subs-card subs-card--loading">
                    <LoadingSkeleton className="h-4 w-24" />
                    <LoadingSkeleton className="h-8 w-3/4" />
                    <LoadingSkeleton className="h-4 w-full" />
                    <LoadingSkeleton className="h-4 w-full" />
                    <LoadingSkeleton className="h-4 w-2/3" />
                  </div>
                ))}
              </div>
            ) : (subsidiaries && subsidiaries.length > 0) ? (
              <div className="subs-grid">
                {subsidiaries.map((sub, i) => {
                  const url = getSubsidiaryUrl(sub.name);
                  const inner = (
                    <>
                      <div className="subs-card-number">{String(i + 1).padStart(2, '0')}</div>
                      <div className="subs-card-body">
                        <span className="subs-card-type">{sub.type}</span>
                        <h2 className="subs-card-name">{sub.name}</h2>
                        {sub.description?.trimStart().startsWith('<')
                          ? <div className="subs-card-desc" dangerouslySetInnerHTML={{ __html: sub.description }} />
                          : <p className="subs-card-desc">{sub.description}</p>
                        }
                      </div>
                      <div className="subs-card-footer">
                        <span className="subs-card-jurisdiction">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden={true}>
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                            <circle cx="12" cy="9" r="2.5"/>
                          </svg>
                          {sub.jurisdiction}
                        </span>
                        {url && (
                          <span className="subs-card-visit">Visit →</span>
                        )}
                      </div>
                    </>
                  );
                  return url ? (
                    <a key={sub._id} href={url} target="_blank" rel="noopener noreferrer" className="subs-card subs-card--link">
                      {inner}
                    </a>
                  ) : (
                    <div key={sub._id} className="subs-card">
                      {inner}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="subs-empty">Subsidiary information coming soon.</p>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
